import React from "react";
import { useSelector } from "react-redux";
import {
  Outlet,
  useParams,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import { RootState } from "../../BusinessObjects/Redux/app/store";
import cubeApiClient from "../../BusinessObjects/cubeApiService";
import { Container } from "reactstrap";
import { Box, Button } from "@mui/material";
import { NavLink } from "react-router-dom";
import { RoutePath } from "../../Utils/RoutePath";
import { ResourceView } from "../../Models/SysConf/ResourceView";
import RenderFormNew from "../../Components/Esaedro/RenderForm/RenderFormNew";
import { useQuery } from "react-query";
import { ResourceNames } from "../../Utils/ResourceNames";
import { Form } from "../../Models/SysConf/Form";
import notify from "devextreme/ui/notify";
import Loader from "../../Utils/Loader";

type ContextType = {
  view: ResourceView;
  setView: React.Dispatch<React.SetStateAction<ResourceView>>;
};

type Props = {};

export default function ResourceViewPage(props: Props) {
  // Redux
  const tokenApi = useSelector((state: RootState) => state.user.tokenAPI);
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

  // React Router DOM
  const { View_ID } = useParams();
  const location = useLocation();

  // Setto sempre la visibilità del form a true quando torno sulla pagina di default
  React.useEffect(() => {
    if (location.pathname === `${RoutePath.RESOURCES_VIEWS}/${View_ID}`) {
      setDefaultFormVisible(true);
    }
  }, [location]);

  // hooks
  const [id, setId] = React.useState<number>(0);
  const [view, setView] = React.useState<ResourceView | undefined>(undefined);
  const [isDefaultFormVisible, setDefaultFormVisible] =
    React.useState<boolean>(true);
  const [isLoadingNow, setLoading] = React.useState<boolean>(false);

  // Al mounted della pagina appena ricevo il View_ID lo setto come id e lo parso a numero per fare la chiamata API
  React.useEffect(() => {
    if (View_ID !== undefined) {
      setId(parseInt(View_ID));
    }
  }, []);

  // Invoco la chiamata api appena l'id viene settato e parsato come numero
  React.useEffect(() => {
    if (id !== 0) {
      fetchView();
    }
  }, [id]);

  // Chiamata api per ricevere i dati della view attraverso l'id
  const fetchView = () => {
    setLoading(true);
    const service = new cubeApiClient();
    service
      .getResourceViewByViewId(tokenApi, id)
      .then((res) => setView(res))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const {
    data: resourcesViews,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    ["resourcesViews", tokenApi],
    () => {
      const service = new cubeApiClient();

      return service.getItems(
        `api/sys/${ResourceNames.ResourcesViewes}`,
        tokenApi
      );
    },
    {
      enabled: isLoggedIn,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const { data: form } = useQuery(
    ["form", ResourceNames.ResourcesViewes, userProfile?.customer_ID, tokenApi],
    () => {
      const service = new cubeApiClient();

      return service
        .getItems(
          `api/sys/${ResourceNames.Resources}?RecordSelection=ResourceName='${ResourceNames.ResourcesViewes}'`,
          tokenApi
        )
        .then(
          (data: any) => {
            let urlForm = `api/sys/${ResourceNames.Forms}/query?ResourceId=${data[0].Resource_ID}`;

            return service.getItems(urlForm, tokenApi).then(
              (data: any) => {
                return data[0];
              },
              (error: any) => {
                return undefined;
              }
            );
          },
          (error: any) => {
            return undefined;
          }
        );
    },
    {
      enabled: isLoggedIn,
      refetchOnWindowFocus: false,
      retry: 0,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  const formResourceView: Form | undefined = React.useMemo(() => {
    if (form) {
      return form;
    }
    return undefined;
  }, [form]);

  const saveNewResourceView = () => {
    const service = new cubeApiClient();

    service
      .updateObject(view, `api/sys/${ResourceNames.ResourcesViewes}`, tokenApi)
      .then(
        (data) => {
          refetch();
          notify("Salvataggio filtro avvenuto con successo", "success", 2000);
        },
        (error) => {
          refetch();
          notify("Errore durante il salvataggio del filtro", "error", 2000);
        }
      );
  };

  return (
    <>
      {formResourceView === undefined || isLoadingNow || view === undefined ? (
        <Loader />
      ) : (
        <Container>
          <Box sx={{ width: "100%", height: "100%", my: 4 }}>
            {/* Navbar con i bottoni per le varie pagine */}
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                mt: 1,
                flexDirection: "row",
                mb: 3,
              }}
            >
              <React.Fragment>
                <NavLink
                  to={`${RoutePath.RESOURCES_VIEWS}/${id}/linked_resources`}
                >
                  <Button
                    variant="contained"
                    onClick={() => setDefaultFormVisible(false)}
                    disabled={
                      location.pathname ===
                      `${RoutePath.RESOURCES_VIEWS}/${id}/linked_resources`
                    }
                  >
                    Risorse Collegate
                  </Button>
                </NavLink>
                <NavLink to={`${RoutePath.RESOURCES_VIEWS}/${id}/field_list`}>
                  <Button
                    variant="contained"
                    onClick={() => setDefaultFormVisible(false)}
                    disabled={
                      location.pathname ===
                      `${RoutePath.RESOURCES_VIEWS}/${id}/field_list`
                    }
                  >
                    Elenco Campi
                  </Button>
                </NavLink>
                <NavLink
                  to={`${RoutePath.RESOURCES_VIEWS}/${id}/fiters_on_fields`}
                >
                  <Button
                    variant="contained"
                    onClick={() => setDefaultFormVisible(false)}
                    disabled={
                      location.pathname ===
                      `${RoutePath.RESOURCES_VIEWS}/${id}/fiters_on_fields`
                    }
                  >
                    Filtri Su Campi
                  </Button>
                </NavLink>
                <NavLink to={`${RoutePath.RESOURCES_VIEWS}/${id}/ordinances`}>
                  <Button
                    variant="contained"
                    onClick={() => setDefaultFormVisible(false)}
                    disabled={
                      location.pathname ===
                      `${RoutePath.RESOURCES_VIEWS}/${id}/ordinances`
                    }
                  >
                    Ordinamenti
                  </Button>
                </NavLink>
              </React.Fragment>
            </Box>

            {isDefaultFormVisible && view ? (
              <RenderFormNew
                isNew={view && view.View_ID !== 0 ? false : true}
                myObj={view}
                form={formResourceView}
                saveNewObject={(e) => {
                  setView(e);
                }}
              />
            ) : (
              <Container>
                <Outlet context={{ view, setView }} />
              </Container>
            )}

            {isDefaultFormVisible && formResourceView && view && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  flexDirection: "row",
                }}
              >
                <NavLink to={RoutePath.RESOURCES_VIEWS}>
                  <Button variant="contained" color="error" sx={{ mr: 1 }}>
                    Annulla
                  </Button>
                </NavLink>
                <NavLink to={RoutePath.RESOURCES_VIEWS}>
                  <Button
                    variant="contained"
                    color="success"
                    sx={{ mr: 1 }}
                    onClick={() => {
                      saveNewResourceView();
                    }}
                  >
                    Salva
                  </Button>
                </NavLink>
              </Box>
            )}
          </Box>
        </Container>
      )}
    </>
  );
}

export function useView() {
  return useOutletContext<ContextType>();
}
